import * as React from "react";
import { useDrawerContext } from "../../../../components/antd/drawerContext";
import { secretTypeActionMap } from "../utils/utils";
import { Button, Space, Tooltip } from "antd";
import { MinusCircleOutlined, MoreOutlined, PlusOutlined } from "@ant-design/icons";
import { SecretTypeTag } from "../../../../components/antd/kindselect/tagRenderers/secretTypeTag";
import { VolumeSetSharedTag } from "../../../../components/antd/kindselect/tagRenderers/volumeSetSharedTag";
import { useVolumeBrowserService } from "./volumesBrowser";
import { VolumesProviderModal } from "./volumeProviderModal";
import { BrowserServiceModal } from "../../../identity/create/browserServiceModal";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../../gvc/testNewForm/utils";
import { CpSelect } from "../../../gvc/testNewForm/CpSelect";
import { CpAutoComplete } from "../../../gvc/testNewForm/CpAutoComplete";
import { CpInput } from "../../../gvc/testNewForm/CpInput";
import { store } from "../../../../store";
import { cloneDeep, get } from "lodash";
import { VolumeInput } from "../../../../schema/types/workload/input";
import { parseLink } from "../../../../utils/linkParser/linkParser";
import { useScratchSuggestions } from "./useScratchSuggestions";
import { toSelectOptions } from "../../../../services/defaults";
import { useCpFormContext } from "../../../../components/form/useCpFormContext";
import { KindSelectState } from "../../../../components/antd/kindselect/KindSelect";
import { CpKindSelect } from "../../../gvc/testNewForm/temp/CpKindSelect";
import { EmbeddedSecretCreate } from "../../../secret/create/embedded";
import { EmbeddedVolumeSetCreate } from "../../../volumeset/create/embedded";

interface Props {
  containerIndex: number;
  index: number;
}
export const VolumeItem: React.FC<Props> = React.memo(({ containerIndex, index }) => {
  const { id } = useCpFormContext();
  const drawer = useDrawerContext();

  const gvc = useCpFormValue(`gvc`);
  const gvcName = React.useMemo(() => {
    const parsedLink = parseLink(gvc.link, { useInputCtx: true });
    return parsedLink.name;
  }, [gvc.link]);

  const uriType = useCpFormValue(`containers.${containerIndex}.volumes.${index}.uriType`);
  const cplnSecret = useCpFormValue(`containers.${containerIndex}.volumes.${index}.cplnSecret`);
  const volumeSet = useCpFormValue(`containers.${containerIndex}.volumes.${index}.volumeSet`);
  const secretCache = useCpFormValue(`meta.secretCache`);

  function remove() {
    const form = store.getState().form.instances[id];
    const values = form.values;
    const items = get(values, `containers.${containerIndex}.volumes`) as VolumeInput[];
    let _items = cloneDeep(items);
    _items.splice(index, 1);
    formSetValue(id, `containers.${containerIndex}.volumes`, _items);

    formTriggerValidation(id);
  }

  const vbs = useVolumeBrowserService();

  let secretPropertySuggestions: string[] = React.useMemo(() => {
    let secretPropertySuggestions: string[] = [];
    if (uriType === "cpln://secret/" && !!cplnSecret) {
      const secretName = cplnSecret.value;
      const cachedSecret = secretCache[secretName];
      if (cachedSecret) {
        const actionMap = secretTypeActionMap[cachedSecret.type];
        if (actionMap.supportsProperties) {
          secretPropertySuggestions = actionMap.properties;
        }
      }
    }
    return secretPropertySuggestions;
  }, [secretCache, cplnSecret, uriType]);

  const scratchSuggestions = useScratchSuggestions(containerIndex, index);

  return (
    <>
      {vbs.page === "provider" && <VolumesProviderModal vbs={vbs} />}
      <BrowserServiceModal
        open={vbs.page === "bucket"}
        title={`Select ${vbs.provider === "azure" ? "Container" : "Bucket"}`}
        cloudaccountLink={vbs.cloudAccountName ? parseLink(vbs.cloudAccountName, { kind: "cloudaccount" }).absolute : ""}
        restrictToBucket={true}
        onClose={(uri) => {
          if (uri.length > 0) {
            let mountUri = uri[0]._cpln.mountUri;
            if (mountUri) {
              const selectedUriType = mountUri.split("://")[0] + "://";
              const uriValue = mountUri.split("://")[1];
              formSetValue(id, `containers.${containerIndex}.volumes.${index}.uriType`, selectedUriType);
              // Set the appropriate field based on URI type
              if (selectedUriType === "s3://") {
                formSetValue(id, `containers.${containerIndex}.volumes.${index}.s3`, uriValue);
              } else if (selectedUriType === "gs://") {
                formSetValue(id, `containers.${containerIndex}.volumes.${index}.gs`, uriValue);
              } else if (selectedUriType === "azureblob://") {
                formSetValue(id, `containers.${containerIndex}.volumes.${index}.azureBlob`, uriValue);
              } else if (selectedUriType === "azurefs://") {
                formSetValue(id, `containers.${containerIndex}.volumes.${index}.azureFs`, uriValue);
              }
            }
          }
          vbs.setPage("none");
          vbs.setCloudAccountName("");
        }}
        selectionType={"single"}
      />
      <CpSelect
        className="col-span-1"
        name={`containers.${containerIndex}.volumes.${index}.uriType`}
        options={[
          { label: "CPLN Secret", value: "cpln://secret/" },
          { label: "K8s Secret", value: "k8s://secret/" },
          { label: "Volume Set", value: "cpln://volumeset/" },
          { label: "AWS S3 Bucket", value: "s3://" },
          { label: "Google Bucket", value: "gs://" },
          { label: "Azure Blob", value: "azureblob://" },
          { label: "Azure File Share", value: "azurefs://" },
          { label: "Scratch", value: "scratch://" },
        ]}
        optionRender={(option) => (
          <div className="flex flex-col gap-1">
            <span>{option.label}</span>
            {option.value ? <span className="text-sm">{option.value}</span> : null}
          </div>
        )}
      />
      <div className="flex items-start gap-1 col-span-1">
        {uriType === "cpln://secret/" ? (
          <>
            <CpKindSelect
              kind="secret"
              name={`containers.${containerIndex}.volumes.${index}.cplnSecret`}
              tagRenderers={[SecretTypeTag] as any}
              optionValue="name"
              className={"flex-grow"}
            />
            {cplnSecret.value ? (
              <CpAutoComplete
                name={`containers.${containerIndex}.volumes.${index}.cplnSecretProperty`}
                style={{ minWidth: 140 }}
                options={secretPropertySuggestions.map((v: string) => ({ label: v, value: v }))}
                placeholder="Property"
              />
            ) : null}
            {cplnSecret.value ? null : (
              <Tooltip title={`Create Secret`}>
                <Button
                  icon={<PlusOutlined />}
                  onClick={() => {
                    drawer.setPersistent(
                      `workload-container-volumes-create-secret`,
                      "Create Secret",
                      <>
                        <EmbeddedSecretCreate
                          onCancel={() => drawer.closePersistent()}
                          onDone={(itemLink, item) => {
                            drawer.closePersistent();
                            const { name } = parseLink(itemLink, { useInputCtx: true });
                            const state: KindSelectState = {
                              value: name,
                              link: itemLink,
                              item: item,
                            };
                            formSetValue(id, `containers.${containerIndex}.volumes.${index}.cplnSecret`, state);
                          }}
                        />
                      </>,
                      600,
                    );
                  }}
                />
              </Tooltip>
            )}
          </>
        ) : uriType === "cpln://volumeset/" ? (
          <Space.Compact className="flex-grow">
            <CpKindSelect
              kind="volumeset"
              name={`containers.${containerIndex}.volumes.${index}.volumeSet`}
              optionValue={`name`}
              disabled={!gvc.value}
              placeholder={!gvc.value ? `Select the GVC first from Workload section` : ""}
              queries={[{ rel: "gvc", value: gvcName || "" }]}
              className={"flex-grow"}
              tagRenderers={[VolumeSetSharedTag] as any}
            />
            {volumeSet.value || !gvc.value ? null : (
              <Tooltip title={`Create Volume Set`}>
                <Button
                  icon={<PlusOutlined />}
                  onClick={() => {
                    drawer.setPersistent(
                      `workload-container-volumes-create-volumeset`,
                      "Create Volume Set",
                      <>
                        <EmbeddedVolumeSetCreate
                          lockedGvc={gvc}
                          onCancel={() => drawer.closePersistent()}
                          onDone={(itemLink, item) => {
                            drawer.closePersistent();
                            const { name } = parseLink(itemLink, { useInputCtx: true });
                            const state: KindSelectState = {
                              value: name,
                              link: itemLink,
                              item: item,
                            };
                            formSetValue(id, `containers.${containerIndex}.volumes.${index}.volumeSet`, state);
                          }}
                        />
                      </>,
                      900,
                    );
                  }}
                />
              </Tooltip>
            )}
          </Space.Compact>
        ) : uriType === "scratch://" ? (
          <CpAutoComplete
            name={`containers.${containerIndex}.volumes.${index}.scratch`}
            style={{ width: "100%" }}
            options={toSelectOptions(scratchSuggestions)}
          />
        ) : uriType === "k8s://secret/" ? (
          <CpInput name={`containers.${containerIndex}.volumes.${index}.k8sSecret`} className={"flex-grow"} />
        ) : uriType === "s3://" ? (
          <>
            <CpInput name={`containers.${containerIndex}.volumes.${index}.s3`} className={"flex-grow"} />
            <Tooltip title={`Browse`}>
              <Button
                htmlType={`button`}
                onClick={() => {
                  vbs.setProvider("aws");
                  vbs.setPage("provider");
                }}
                icon={<MoreOutlined />}
              />
            </Tooltip>
          </>
        ) : uriType === "gs://" ? (
          <>
            <CpInput name={`containers.${containerIndex}.volumes.${index}.gs`} className={"flex-grow"} />
            <Tooltip title={`Browse`}>
              <Button
                htmlType={`button`}
                onClick={() => {
                  vbs.setProvider("gcp");
                  vbs.setPage("provider");
                }}
                icon={<MoreOutlined />}
              />
            </Tooltip>
          </>
        ) : uriType === "azureblob://" ? (
          <>
            <CpInput name={`containers.${containerIndex}.volumes.${index}.azureBlob`} className={"flex-grow"} />
            <Tooltip title={`Browse`}>
              <Button
                htmlType={`button`}
                onClick={() => {
                  vbs.setProvider("azure");
                  vbs.setPage("provider");
                }}
                icon={<MoreOutlined />}
              />
            </Tooltip>
          </>
        ) : uriType === "azurefs://" ? (
          <>
            <CpInput name={`containers.${containerIndex}.volumes.${index}.azureFs`} className={"flex-grow"} />
            <Tooltip title={`Browse`}>
              <Button
                htmlType={`button`}
                onClick={() => {
                  vbs.setProvider("azure");
                  vbs.setPage("provider");
                }}
                icon={<MoreOutlined />}
              />
            </Tooltip>
          </>
        ) : null}
      </div>
      <div className={"col-span-1 flex gap-2"}>
        <CpInput name={`containers.${containerIndex}.volumes.${index}.path`} />
        <Button type={`text`} danger icon={<MinusCircleOutlined />} onClick={() => remove()} />
      </div>
    </>
  );
});
